import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import type { VisitQr } from '@gymboard/types'
import { getVisitQr } from '../lib/visits-api'

/** Fetch the next code this long before the current one expires. */
const REFRESH_LEAD_MS = 4_000
const RETRY_MS = 5_000
/** While the gym has floor check-in off, check back this often. */
const OFF_POLL_MS = 60_000

/**
 * The rotating gym-floor check-in code for this screen. The code expires
 * every 30 s (proof of presence), so it's fetched again just before each one
 * runs out. `off` is true while the gym has floor check-in turned off; it
 * flips back by itself once an admin turns it on.
 */
export function useVisitQr(): { qr: VisitQr | null; off: boolean } {
  const [qr, setQr] = useState<VisitQr | null>(null)
  const [off, setOff] = useState(false)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false
    const load = async () => {
      try {
        const { data } = await getVisitQr()
        if (cancelled) return
        setQr(data)
        setOff(false)
        timer = setTimeout(load, Math.max(1_000, data.expiresAt - Date.now() - REFRESH_LEAD_MS))
      } catch (err) {
        if (cancelled) return
        const isOff = isAxiosError(err) && err.response?.status === 403
        if (isOff) {
          setOff(true)
          setQr(null)
        }
        timer = setTimeout(load, isOff ? OFF_POLL_MS : RETRY_MS)
      }
    }
    void load()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [])

  return { qr, off }
}
